import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUp,
  ArrowUpRight,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { Container } from "./Container";
import { COMPANY_INFO, FOOTER_LINKS } from "../../utils/constants";
import navLogo from "../../logos/nav-logo-lg-mode.png";

const LinkedinIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const socialIcons = {
  linkedin: LinkedinIcon,
  instagram: InstagramIcon,
};

function FooterColumn({ title, links }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
        {title}
      </h4>
      <ul className="mt-5 space-y-3">
        {links.map((link) => (
          <li key={link.path}>
            <Link
              to={link.path}
              className="group inline-flex items-center gap-1 text-white/70 hover:text-white transition-colors"
            >
              <span className="relative">
                {link.name}
                <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-primary-400 transition-all duration-300 group-hover:w-full" />
              </span>
              <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-dark-950 text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[50rem] h-[26rem] rounded-full bg-primary-600/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:48px_48px] [mask-image:linear-gradient(to_bottom,black,transparent_60%)]"
      />

      <Container className="relative">
        {/* CTA band */}
        <div className="flex flex-col gap-8 border-b border-white/10 py-14 md:py-20 lg:flex-row lg:items-end lg:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] px-3 py-1 text-xs font-semibold text-white/70 ring-1 ring-white/10">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-green opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-accent-green" />
              </span>
              Taking new projects
            </span>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance">
              Have an idea?{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-300 via-accent-blue to-primary-400">
                Let's build it.
              </span>
            </h2>
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="group mt-5 inline-flex items-center gap-2 text-lg text-white/60 hover:text-white transition-colors"
            >
              {COMPANY_INFO.email}
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, damping: 18 }}
          >
            <Link
              to="/contact"
              className="group relative grid place-items-center w-36 h-36 sm:w-40 sm:h-40 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-center font-semibold shadow-glow-lg transition-transform duration-500 hover:scale-105"
            >
              <span className="absolute inset-0 rounded-full ring-1 ring-white/30 animate-ping [animation-duration:3s]" />
              <span className="relative flex flex-col items-center gap-1">
                Start a project
                <ArrowUpRight className="w-6 h-6 transition-transform duration-500 group-hover:rotate-45" />
              </span>
            </Link>
          </motion.div>
        </div>

        {/* Links */}
        <div className="grid grid-cols-2 gap-10 py-14 md:grid-cols-4 lg:grid-cols-12 lg:gap-8">
          <div className="col-span-2 md:col-span-4 lg:col-span-4">
            <Link to="/" className="inline-block" aria-label="Techoside home">
              <img
                src={navLogo}
                alt="Techoside"
                className="h-9 w-auto brightness-0 invert"
              />
            </Link>
            <p className="mt-5 max-w-sm text-white/60 leading-relaxed">
              {COMPANY_INFO.description}
            </p>
            <div className="mt-6 flex gap-2">
              {Object.entries(COMPANY_INFO.social).map(([platform, url]) => {
                const Icon = socialIcons[platform];
                if (!Icon) return null;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grid place-items-center w-11 h-11 rounded-xl bg-white/[0.06] text-white/70 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-1 hover:bg-primary-500 hover:text-white hover:ring-primary-400"
                    aria-label={`Follow us on ${platform}`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <FooterColumn title="Company" links={FOOTER_LINKS.quickLinks} />
          </div>
          <div className="lg:col-span-3">
            <FooterColumn title="Services" links={FOOTER_LINKS.services} />
          </div>

          <div className="col-span-2 md:col-span-2 lg:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
              Contact
            </h4>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="group flex items-center gap-3 rounded-2xl bg-white/[0.04] p-3 ring-1 ring-white/10 transition-colors hover:bg-white/[0.08]"
                >
                  <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-primary-500/15 text-primary-300">
                    <Mail className="w-4 h-4" />
                  </span>
                  <span className="min-w-0 truncate text-sm text-white/80 group-hover:text-white">
                    {COMPANY_INFO.email}
                  </span>
                </a>
              </li>
              <li className="flex items-center gap-3 rounded-2xl bg-white/[0.04] p-3 ring-1 ring-white/10">
                <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-primary-500/15 text-primary-300">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="flex flex-col text-sm">
                  {[COMPANY_INFO.phone, COMPANY_INFO.phone2].map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone}`}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      {phone}
                    </a>
                  ))}
                </span>
              </li>
              <li className="flex items-center gap-3 rounded-2xl bg-white/[0.04] p-3 ring-1 ring-white/10">
                <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-primary-500/15 text-primary-300">
                  <MapPin className="w-4 h-4" />
                </span>
                <span className="text-sm text-white/80">
                  {COMPANY_INFO.address}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Container>

      {/* Wordmark */}
      <div aria-hidden="true" className="relative select-none overflow-hidden">
        <motion.div
          initial={{ y: "40%", opacity: 0 }}
          whileInView={{ y: "0%", opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="text-center font-display font-bold leading-[0.8] tracking-tighter text-[18vw] bg-clip-text text-transparent bg-gradient-to-b from-white/[0.12] to-white/0 -mb-[3vw]"
        >
          techoside
        </motion.div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <Container>
          <div className="flex flex-col items-center justify-between gap-4 py-6 text-sm md:flex-row">
            <p className="text-white/40">
              © {currentYear} {COMPANY_INFO.name}. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {FOOTER_LINKS.legal.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-white/40 hover:text-white transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="group inline-flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
              >
                Back to top
                <ArrowUp className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
}
